<template>
  <div v-if="isVisible" class="onboarding-tour box">
    <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
      <h3 class="title is-5 mb-0">{{ currentStepData.title }}</h3>
      <span class="tag is-warning">Step {{ currentStep + 1 }} of {{ steps.length }}</span>
    </div>
    <p class="mb-4">{{ currentStepData.description }}</p>
    <div class="buttons">
      <button 
        class="button is-small" 
        :disabled="currentStep === 0"
        @click="previousStep">
        <span class="icon is-small">
          <FontAwesomeIcon :icon="faChevronLeft" />
        </span>
        <span>Back</span>
      </button>
      <button 
        class="button is-small is-warning" 
        :disabled="isWaitingForAction"
        @click="nextStep">
        <span>{{ isLastStep ? 'Finish' : 'Next' }}</span>
        <span class="icon is-small">
          <FontAwesomeIcon :icon="isLastStep ? faCheck : faChevronRight" />
        </span>
      </button>
      <button 
        class="button is-small is-text" 
        @click="skipTour">
        Skip Tour
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useBlocksStore } from '../stores/blocks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faChevronRight, faCheck } from '@fortawesome/free-solid-svg-icons';

const blocksStore = useBlocksStore();

// Storage keys
const COMPLETED_KEY = 'timist_onboarding_completed';
const DISMISSED_KEY = 'timist_onboarding_dismissed';
const USAGE_COUNT_KEY = 'timist_usage_count';

// Visibility state
const isVisible = ref(false);
const currentStep = ref(0);

// Track initial state for detecting user actions
const initialBlockCount = ref(0);
const hasCreatedTimer = ref(false);
const hasStartedTimer = ref(false);

// Tour steps configuration
const steps = [
  {
    targetSelector: '#task-input',
    title: 'Create Your First Timer',
    description: 'Type what you want to work on (e.g., "Give Timist a try"), choose a duration (25 or 40 minutes), then hit Enter or click the Add button.',
    action: 'focus',
    waitForAction: 'timer-created'
  },
  {
    targetSelector: '[data-onboarding-target="timer-block"]',
    title: 'Your Time Block',
    description: 'Here\'s your scheduled timer! Drag the top bar to move it to a different time, or drag the bottom handle to adjust its duration.',
    action: 'highlight-with-handles',
    waitForAction: false
  },
  {
    targetSelector: '[data-onboarding-target="timer-block"] .button.is-primary',
    title: 'Start Tracking',
    description: 'Click the play button to start tracking your time. The timer will run until you manually stop it.',
    action: 'highlight-button',
    waitForAction: 'timer-started'
  },
  {
    targetSelector: '#active-timer-display .button',
    title: 'Active Timer Display',
    description: 'Your running timer appears here! You can see elapsed time and click to stop it or view details.',
    action: 'highlight-button',
    waitForAction: false
  },
  {
    targetSelector: '[data-onboarding-target="timer-block"]',
    title: 'View Details',
    description: 'Click anywhere on your timer block to open the details modal. There you can edit your timer, view history, and manage completed sessions. That\'s it - you\'re ready to be productive!',
    action: 'highlight-background',
    waitForAction: false
  }
];

// Computed properties
const currentStepData = computed(() => steps[currentStep.value] || {});
const isLastStep = computed(() => currentStep.value === steps.length - 1);
const isWaitingForAction = computed(() => {
  if (!currentStepData.value.waitForAction) return false;
  
  if (currentStepData.value.waitForAction === 'timer-created') {
    return !hasCreatedTimer.value;
  }
  
  if (currentStepData.value.waitForAction === 'timer-started') {
    return !hasStartedTimer.value;
  }
  
  return false;
});

// Check if onboarding should be shown
const shouldShowOnboarding = () => {
  // Only on desktop
  if (window.innerWidth < 1024) return false;
  
  // Only in frontend-only mode
  if (!blocksStore.isFrontendOnly) return false;
  
  // Check if already completed or dismissed
  if (localStorage.getItem(COMPLETED_KEY) === 'true') return false;
  if (localStorage.getItem(DISMISSED_KEY) === 'true') return false;
  
  // Check for significant usage
  const usageCount = parseInt(localStorage.getItem(USAGE_COUNT_KEY) || '0');
  const existingBlocks = blocksStore.blocks?.length || 0;
  
  if (usageCount > 3 || existingBlocks >= 2) return false;
  
  return true;
};

// Highlighting logic
const highlightedElement = ref(null);
let highlightTimeoutId = null;

const clearHighlight = () => {
  // Cancel any pending highlight operation
  if (highlightTimeoutId) {
    clearTimeout(highlightTimeoutId);
    highlightTimeoutId = null;
  }
  
  // Always try to clear ALL onboarding classes from the entire document
  const allHighlightedElements = document.querySelectorAll(
    '.onboarding-highlight, .onboarding-highlight-button, .onboarding-highlight-background, .onboarding-focus, .onboarding-highlight-handle'
  );
  allHighlightedElements.forEach(element => {
    element.classList.remove('onboarding-highlight');
    element.classList.remove('onboarding-highlight-button');
    element.classList.remove('onboarding-highlight-background');
    element.classList.remove('onboarding-focus');
    element.classList.remove('onboarding-highlight-handle');
  });
  
  highlightedElement.value = null;
};

const highlightElement = (selector, action) => {
  clearHighlight();
  
  // Small delay to ensure DOM is ready
  highlightTimeoutId = setTimeout(() => {
    // Don't highlight if tour is no longer visible
    if (!isVisible.value) return;
    
    const element = document.querySelector(selector);
    if (!element) {
      console.warn(`Onboarding: Element not found: ${selector}`);
      return;
    }
    
    if (action === 'focus') {
      element.focus();
      element.classList.add('onboarding-focus');
      highlightedElement.value = element;
    } else if (action === 'highlight') {
      element.classList.add('onboarding-highlight');
      highlightedElement.value = element;
    } else if (action === 'highlight-button') {
      element.classList.add('onboarding-highlight-button');
      highlightedElement.value = element;
    } else if (action === 'highlight-background') {
      element.classList.add('onboarding-highlight-background');
      highlightedElement.value = element;
    } else if (action === 'highlight-with-handles') {
      // Don't highlight the block itself, just the handles
      highlightedElement.value = element;
      
      // Highlight the drag and resize handles
      const dragHandle = element.querySelector('.drag-handle');
      const resizeHandle = element.querySelector('.resize-handle');
      if (dragHandle) {
        dragHandle.classList.add('onboarding-highlight-handle');
      }
      if (resizeHandle) {
        resizeHandle.classList.add('onboarding-highlight-handle');
      }
    }
  }, 100);
};

// Navigation methods
const nextStep = () => {
  if (isWaitingForAction.value) return;
  
  if (isLastStep.value) {
    completeTour();
  } else {
    currentStep.value++;
    updateHighlight();
  }
};

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--;
    updateHighlight();
  }
};

const skipTour = () => {
  clearHighlight();
  localStorage.setItem(DISMISSED_KEY, 'true');
  isVisible.value = false;
};

const completeTour = () => {
  clearHighlight();
  localStorage.setItem(COMPLETED_KEY, 'true');
  isVisible.value = false;
};

const updateHighlight = () => {
  const stepData = currentStepData.value;
  if (stepData.targetSelector && stepData.action) {
    highlightElement(stepData.targetSelector, stepData.action);
  }
};

// Watchers for user actions
watch(() => blocksStore.blocks.length, (newLength) => {
  if (newLength > initialBlockCount.value && !hasCreatedTimer.value) {
    hasCreatedTimer.value = true;
    // Track usage
    const count = parseInt(localStorage.getItem(USAGE_COUNT_KEY) || '0');
    localStorage.setItem(USAGE_COUNT_KEY, (count + 1).toString());
    
    // Auto-advance if waiting on this step
    if (currentStep.value === 0 && currentStepData.value.waitForAction === 'timer-created') {
      setTimeout(() => {
        nextStep();
      }, 500);
    }
  }
});

watch(() => blocksStore.activeTimer, (timer) => {
  if (timer && !hasStartedTimer.value) {
    hasStartedTimer.value = true;
    
    // Auto-advance if waiting on this step
    if (currentStep.value === 2 && currentStepData.value.waitForAction === 'timer-started') {
      setTimeout(() => {
        nextStep();
      }, 500);
    }
  }
});

// Lifecycle
onMounted(() => {
  if (shouldShowOnboarding()) {
    initialBlockCount.value = blocksStore.blocks?.length || 0;
    
    // Delay to ensure UI is rendered
    setTimeout(() => {
      isVisible.value = true;
      updateHighlight();
    }, 800);
  }
});

onUnmounted(() => {
  clearHighlight();
});
</script>

<style scoped>
.onboarding-tour {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  max-width: 420px;
  z-index: 1000;
  box-shadow: var(--bulma-shadow-large);
  border: 2px solid hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l));
  background-color: hsl(var(--bulma-scheme-h), var(--bulma-scheme-s), var(--bulma-scheme-main-bis-l));
}

@media screen and (max-width: 1023px) {
  .onboarding-tour {
    display: none;
  }
}

@media screen and (max-width: 768px) {
  .onboarding-tour {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
}
</style>

<style>
/* Global styles for onboarding highlights (not scoped so they can affect any element) */
.onboarding-focus {
  border-color: hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l)) !important;
  box-shadow: 0 0 0 0.125em hsla(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l), 0.25) !important;
}

.onboarding-highlight {
  outline: 3px solid hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l)) !important;
  outline-offset: 2px;
  box-shadow: 0 0 0 6px hsla(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l), 0.25) !important;
  position: relative;
  z-index: 999;
  transition: all 0.3s ease;
}

/* Show warning gradient on handles during onboarding - make them visible! */
.resize-handle.onboarding-highlight-handle {
  background: linear-gradient(to bottom, transparent, hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l)));
  opacity: 0.7;
  transition: all 0.3s ease;
}

.drag-handle.onboarding-highlight-handle {
  background: linear-gradient(to top, transparent, hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l)));
  opacity: 0.7;
  transition: all 0.3s ease;
}

/* Allow default hover/active states to override onboarding colors */
.resize-handle.onboarding-highlight-handle:hover {
  background: linear-gradient(to bottom, transparent, var(--bulma-link));
}

.resize-handle.onboarding-highlight-handle:active {
  background: linear-gradient(to bottom, transparent, var(--bulma-link));
}

.drag-handle.onboarding-highlight-handle:hover {
  background: linear-gradient(to top, transparent, var(--bulma-link));
}

.drag-handle.onboarding-highlight-handle:active {
  background: linear-gradient(to top, transparent, var(--bulma-link));
}

/* Make buttons warning colored during onboarding */
.onboarding-highlight-button {
  background-color: hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l)) !important;
  border-color: hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l)) !important;
  color: hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l-invert)) !important;
  transition: all 0.3s ease;
}

/* Highlight background in warning for clickable hint */
.onboarding-highlight-background {
  background-color: hsl(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l)) !important;
  cursor: pointer !important;
  transition: all 0.3s ease;
}
</style>
